import { trackEvent } from "../analytics/analytics";
import apiClient, { BaseOperationResult } from "../../services/apiClient";
import { DataType } from "../data/data-types";

export const CsvJobId = "csv-import";

export type CsvField = {
  name: string;
  value: string;
  dataType?: DataType;
  ignore?: boolean;
  isVariant?: boolean;
};

export type CsvRecord = {
  fields: CsvField[];
  subRecords: CsvRecord[];
  collectionNames: string[];
  index: number;
  code: string;
  /** The name the product would get: a Name column, the picked name column, or the code. */
  name?: string;
  categoryName: string;
};

export type MetaRecord = {
  name: string;
  count: number;
  checked?: boolean;
};

export type FieldRecord = {
  dataType: string;
} & MetaRecord;

export type CategoryRecord = {
  fields: FieldRecord[];
  options: MetaRecord[];
  isNew: boolean;
} & MetaRecord;

export type CsvError = {
  message: string;
  code: string;
  index: number;
  isWarning?: boolean;
  count: number;
};

export type CsvAnalysys = {
  categories: CategoryRecord[];
  collections: MetaRecord[];
  recordsCount: number;
  headers: string[];
  categoryChanged: boolean;
  categoryAdded: number;
  resolved?: boolean;
  limitExceeded?: number;
  codeHeaderAbsent?: boolean;
  nameHeaderAbsent?: boolean;
  // Every new product is anonymous (no name column detected/mapped). Blocks
  // commit until the user picks a name column or confirms "use code as name".
  nameUnresolved?: boolean;
  missedCode?: number;
  missedName?: number;
  missedCategories?: number;
  productMoved?: number;
  // Values a blank cell would erase from products that already exist. A blank cell
  // means "clear this field", which is right for an export/edit/re-import round trip
  // and wrong for a hand-built partial CSV - so we state it instead of guessing.
  clearedFields?: ClearedFieldRecord[];
  clearedValues?: number;
  // S112: where Price, Image and Description are read from, and how many new products get each.
  essentials?: EssentialColumn[];
  newProducts?: number;
  // "woocommerce" | "shopify": the file is that store's own export. Pending until the user
  // chooses the store import or to go on with the file.
  storeExport?: string;
  storeExportPending?: boolean;
};

export type EssentialColumn = {
  field: "Price" | "Image" | "Description";
  /** The header the field is read from; empty when none. */
  column?: string;
  /** picked | remembered | literal | synonym | word | none */
  how: string;
  /** New products that will hold a value for it. Absent when 0 (the response omits defaults). */
  filled?: number;
};

/** The "none" choice of an essential's column picker (an empty value means "detect"). */
export const NO_COLUMN = "-";

export type ClearedFieldRecord = {
  name: string;
  count: number;
};

export type CsvResult = {
  createLimit: number;
  maxLimit: number;
  fileName: string;
  /** Set for a workbook with several data sheets: which one was imported and which were not. */
  sheetNote?: string;
  analysis: CsvAnalysys;
  records: CsvRecord[];
  errors: CsvError[];
  jobId?: string;
  errorCount?: number;
  warningCount?: number;
  count?: number;
  criticalStop?: boolean;
};

export type CsvImportOptions = {
  categoryId?: string;
  importText?: string;
  importMode?: string;
  nameField?: string;
  codeField?: string;
  // Where new products with no category go: an existing category (`categoryId`), a new one
  // created by name, or the value of a column in the file. `categoryMode` is client-only.
  categoryMode?: "existing" | "new" | "column";
  newCategory?: string;
  categoryColumn?: string;
  // Beside a category column: the category for rows whose cell is blank. Client-only, sent as
  // `categoryId`. Empty means those rows are skipped.
  fallbackCategoryId?: string;
  // An empty cell keeps the value a product already has. On unless the user unticks it.
  ignoreEmptyCells?: boolean;
  preview?: boolean;
  collectionId?: string;
  // Client-only acknowledgement that a name-less catalog should use the product
  // code as the name. Unblocks the import gate; the server already defaults to
  // code-as-name when NameField is empty, so it is not sent/read server-side.
  useCodeAsName?: boolean;
  // S112: the columns picked for the fields every category has. NO_COLUMN is "none".
  priceField?: string;
  imageField?: string;
  descriptionField?: string;
  // A store's export goes on as a file instead of the store import.
  keepStoreFile?: boolean;
};

/** The block where new categories, fields and collections are approved; the modal scrolls to it. */
export const APPROVE_SECTION_ID = "csv-import-approve";

/** Categories with something new that the user has not ticked yet. */
export const countNotApproved = (result: CsvResult) =>
  result?.analysis?.categories?.filter((c) => !c.checked).length ?? 0;

export const syncMeta = async (
  analysis: CsvAnalysys
): Promise<BaseOperationResult> => {
  trackEvent({ category: "csv-import", action: "sync-meta" });
  const response = await apiClient.post<BaseOperationResult>(
    "/data/sync-meta",
    analysis
  );
  return response.data;
};
